import { ReactNode, useState, useEffect } from "react";

import type { ToastActionElement, ToastProps } from "@/components/ui/toast";

const TOAST_LIMIT = 5;
const TOAST_DURATION_DEFAULT = 5000;
const TOAST_DURATION_DESTRUCTIVE = 8000;

type ToasterToast = ToastProps & {
  id: string;
  title?: ReactNode;
  description?: ReactNode;
  action?: ToastActionElement;
};

type ActionTypeMap = {
  ADD_TOAST: "ADD_TOAST";
  UPDATE_TOAST: "UPDATE_TOAST";
  DISMISS_TOAST: "DISMISS_TOAST";
  REMOVE_TOAST: "REMOVE_TOAST";
};

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type Action =
  | {
      type: ActionTypeMap["ADD_TOAST"];
      toast: ToasterToast;
    }
  | {
      type: ActionTypeMap["UPDATE_TOAST"];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionTypeMap["DISMISS_TOAST"];
      toastId?: string;
    }
  | {
      type: ActionTypeMap["REMOVE_TOAST"];
      toastId?: string;
    };

interface State {
  toasts: ToasterToast[];
}

/**
 * Updates the state of toast notifications based on the given action.
 * @example
 * stateReducer(currentState, { type: "ADD_TOAST", toast: newToast })
 * // Returns the updated state with newToast added to the list.
 * @param {Object} state - The current state of the toast system.
 * @param {Object} action - The action to perform, including type and required data.
 * @returns {Object} The new state after applying the given action.
 * @description
 *   - The function handles four actions: ADD_TOAST, UPDATE_TOAST, DISMISS_TOAST, and REMOVE_TOAST.
 *   - It maintains the toast list within the bounds defined by TOAST_LIMIT when adding toasts.
 *   - Clearing timeouts in DISMISS_TOAST ensures proper cleanup of pending toast timeouts.
 *   - REMOVE_TOAST action can clear all toasts or remove specific ones based on toastId.
 */
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

// Toast type alias for convenience (extends ToasterToast without id)
type Toast = Omit<ToasterToast, "id">;

/**
 * Creates and manages a toast notification with unique ID.
 * @example
 * toast({ message: 'Hello World', duration: 5000 })
 * // Returns: { id: 'generatedId123', dismiss: Function, update: Function }
 * @param {Toast} props - Initial properties for the toast message.
 * @returns {Object} Object containing toast id, dismiss function, and update function.
 * @description
 *   - Generate a unique ID for each toast notification.
 *   - Allows updating of the toast's properties and dismiss the toast.
 *   - Automatically handles toast open state changes.
 *   - Integrates with a notification management system via dispatch actions.
 */
function toast({ duration, variant, ...props }: Toast) {
  const id = genId();
  const resolvedDuration =
    duration ?? (variant === "destructive" ? TOAST_DURATION_DESTRUCTIVE : TOAST_DURATION_DEFAULT);

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      variant,
      duration: resolvedDuration,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return {
    id,
    dismiss,
    update,
  };
}

/**
 * Custom hook for managing toast notifications in a React application.
 * @example
 * const { toast, dismiss } = useToast();
 * toast({ message: "Sample notification" });
 * dismiss("toastId123");
 * @param {State} memoryState - Initial state of the toast notifications.
 * @returns {object} An object containing current toast state, toast function, and dismiss function.
 * @description
 *   - Uses React's useState and useEffect to manage state and side-effects respectively.
 *   - Ensures that the state update function is added to and removed from a listeners array to synchronize state across components.
 *   - Returns utilities for adding and removing toast notifications.
 */
function useToast() {
  const [state, setState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  };
}

export { useToast, toast };
